import type { CardTemplate } from '@molt/shared';
import { CopyPlus, X } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactElement } from 'react';

import { Button, Dialog, EmptyState, IconButton, Notice, TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * FE-80.1 card-template create dialog — the dialog the card composer's
 * `Create from template` button opens, and the one the card back's Actions
 * menu opens too.
 *
 * ⚠ This file had 22 English strings and ZERO `t(` call sites, and it is the
 * reason the keys live in a top-level `cardTemplateCreate.*` family rather
 * than in `boardFace.*`: it has TWO callers in TWO route namespaces —
 * `ListColumn.tsx` (board face) and `CardBack.tsx` (card back). Neither passes
 * any English in; all 22 strings are authored here. The full ruling, and the
 * precedent this sets for the five sibling dialogs with the same shape, is in
 * the catalogue beside the declarations.
 *
 * ⚠ `t` is destructured ABOVE the `if (!open) return null` guard. It is a hook
 * call and cannot sit after an early return.
 *
 * ⚠ The `<option>` label is ONE slotted key, not a name with a fragment glued
 * after it. This file used to render two adjacent JSX expression children —
 * the empty-title fallback and a bare ` - hidden from list` — which is the
 * two-fragment shape a translator cannot reorder. `displayTitle` now feeds the
 * slot, so an untitled-AND-hidden template still renders both halves. The
 * rendered `textContent` is unchanged: JSX stripped the whitespace between the
 * two expression children, and `FR-TMPL-011.spec.ts:219` binds the result.
 */

export interface CardTemplateCreateSubmit {
  readonly templateCardId: string;
  readonly title: string;
}

export interface CardTemplateCreateDialogProps {
  readonly open: boolean;
  readonly templates: readonly CardTemplate[];
  readonly defaultTemplateCardId?: string | undefined;
  readonly destinationListTitle: string;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly createdCardPath?: string | undefined;
  readonly onCancel: () => void;
  readonly onCreate: (input: CardTemplateCreateSubmit) => void;
}

export function CardTemplateCreateDialog({
  open,
  templates,
  defaultTemplateCardId,
  destinationListTitle,
  loading,
  busy,
  createdCardPath,
  onCancel,
  onCreate,
}: CardTemplateCreateDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const titleField = useRef<HTMLInputElement | null>(null);
  const formId = useId();
  const templateSelectId = useId();
  const cleanDestinationListTitle =
    destinationListTitle.trim() === ''
      ? t('cardTemplateCreate.destinationFallback')
      : destinationListTitle.trim();

  const [title, setTitle] = useState('');
  const [templateCardId, setTemplateCardId] = useState('');
  const [titleTouched, setTitleTouched] = useState(false);

  const defaultSelectedTemplateCardId = useMemo(() => {
    if (
      defaultTemplateCardId !== undefined &&
      templates.some((template) => template.id === defaultTemplateCardId)
    ) {
      return defaultTemplateCardId;
    }
    return templates[0]?.id ?? '';
  }, [defaultTemplateCardId, templates]);

  const selectedTemplateCardId = templates.some(
    (template) => template.id === templateCardId,
  )
    ? templateCardId
    : defaultSelectedTemplateCardId;

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setTemplateCardId(defaultSelectedTemplateCardId);
    setTitleTouched(false);
  }, [defaultSelectedTemplateCardId, open]);

  if (!open) return null;

  const hasTemplates = templates.length > 0;
  const titleError =
    title.trim().length === 0 ? t('cardTemplateCreate.titleRequired') : null;
  const visibleTitleError = titleTouched ? titleError : null;
  const controlsDisabled = busy === true || loading === true || !hasTemplates;
  const confirmDisabled =
    controlsDisabled || selectedTemplateCardId === '' || titleError !== null;
  const disabledReason =
    loading === true
      ? t('cardTemplateCreate.loadingReason')
      : !hasTemplates
        ? t('cardTemplateCreate.noTemplatesBody')
        : (titleError ?? undefined);
  const disabledReasonProps = disabledReason ? { disabledReason } : {};
  const busyProps = busy === undefined ? {} : { busy };
  const resultVisible = createdCardPath !== undefined;

  function submitCreate() {
    setTitleTouched(true);
    if (confirmDisabled) return;
    onCreate({
      templateCardId: selectedTemplateCardId,
      title: title.trim(),
    });
  }

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('cardTemplateCreate.title')}
      description={t('cardTemplateCreate.description', {
        list: cleanDestinationListTitle,
      })}
      initialFocus={titleField}
      closeControl={
        <IconButton label={t('cardTemplateCreate.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {resultVisible
              ? t('cardTemplateCreate.done')
              : t('cardTemplateCreate.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<CopyPlus aria-hidden="true" className="size-4" />}
            busyLabel={t('cardTemplateCreate.busy')}
            disabled={confirmDisabled}
            type="submit"
            form={formId}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('cardTemplateCreate.submit')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submitCreate();
        }}
      >
        {resultVisible ? (
          <Notice
            tone="info"
            title={t('cardTemplateCreate.resultTitle')}
            body={t('cardTemplateCreate.resultBody', { list: cleanDestinationListTitle })}
            action={
              <Button as="a" size="sm" variant="secondary" href={createdCardPath}>
                {t('cardTemplateCreate.openCreated')}
              </Button>
            }
          />
        ) : null}

        <TextField
          ref={titleField}
          label={t('cardTemplateCreate.titleLabel')}
          value={title}
          required
          disabled={controlsDisabled}
          onBlur={() => setTitleTouched(true)}
          onChange={(event) => {
            setTitleTouched(true);
            setTitle(event.currentTarget.value);
          }}
          {...(visibleTitleError === null ? {} : { error: visibleTitleError })}
        />

        {loading === true && !hasTemplates ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('cardTemplateCreate.loadingTitle')}
            body={t('cardTemplateCreate.loadingBody')}
          />
        ) : hasTemplates ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={templateSelectId} className="text-sm font-medium text-ink">
              {t('cardTemplateCreate.templateLabel')}
            </label>
            <select
              id={templateSelectId}
              value={selectedTemplateCardId}
              disabled={controlsDisabled}
              onChange={(event) => setTemplateCardId(event.currentTarget.value)}
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent disabled:cursor-not-allowed disabled:text-ink-muted"
            >
              {templates.map((template) => {
                const displayTitle =
                  template.title.trim() === ''
                    ? t('cardTemplateCreate.untitledTemplate')
                    : template.title;
                return (
                  <option key={template.id} value={template.id}>
                    {template.hiddenFromList
                      ? t('cardTemplateCreate.hiddenOption', { title: displayTitle })
                      : displayTitle}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('cardTemplateCreate.noTemplatesTitle')}
            body={t('cardTemplateCreate.noTemplatesBody')}
          />
        )}
      </form>
    </Dialog>
  );
}
